import { useCallback, useContext, useMemo } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";
import {
    clickSpecialInstructions,
    impressionSpecialInstructions,
    clickSaveInstructions,
} from "@cart-client/analytics/events/SpecialInstructions";
import {
    clickHalfCardSubmitRequest,
    clickHalfCardRemoveRequest,
    clickHalfCardUpdateRequest,
    clickSpecialInstructionsPill,
    impressionHalfCardSpecialInstruction,
    impressionSpecialInstructionsPill,
    clickHalfCardClose,
} from "@cart-client/analytics/events/SpecialInstructionsHalfCard";
import { getRestaurantId } from "@cart-client/helpers/Cart";
import { ReviewedCart } from "@cart-client/interfaces/Cart";

interface UseRxInstructionsAnalyticsProps {
    cartResponse: ReviewedCart | null;
    note: string;
    cartId: string;
    isRxInstructionsHalfCardXPEnabled: boolean;
}

export function useRxInstructionsAnalytics({
    cartResponse,
    note,
    cartId,
    isRxInstructionsHalfCardXPEnabled,
}: UseRxInstructionsAnalyticsProps) {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const sid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid);

    const restaurantId = useMemo(() => getRestaurantId(cartResponse), [cartResponse]);

    const specialInstructionsHalfCardAnalyticsData = useMemo(
        () => ({
            cartId,
            sid,
            rid: restaurantId,
        }),
        [cartId, sid, restaurantId],
    );

    const onSpecialInstructionsImpression = useCallback(() => {
        if (cartResponse) {
            if (!isRxInstructionsHalfCardXPEnabled) {
                impressionSpecialInstructions(restaurantId, impressionEvent);
            } else {
                impressionSpecialInstructionsPill({
                    isEdit: !!note,
                    ...specialInstructionsHalfCardAnalyticsData,
                    impressionEvent,
                });
            }
        }
    }, [
        cartResponse,
        isRxInstructionsHalfCardXPEnabled,
        impressionEvent,
        note,
        specialInstructionsHalfCardAnalyticsData,
        restaurantId,
    ]);

    const onSpecialInstructionsClick = useCallback(
        (isClosed?: boolean) => {
            if (cartResponse) {
                if (!isRxInstructionsHalfCardXPEnabled) {
                    clickSpecialInstructions(restaurantId, clickEvent, isClosed);
                } else {
                    clickSpecialInstructionsPill({
                        isEdit: !!note,
                        ...specialInstructionsHalfCardAnalyticsData,
                        clickEvent,
                    });
                }
            }
        },
        [
            cartResponse,
            clickEvent,
            isRxInstructionsHalfCardXPEnabled,
            note,
            specialInstructionsHalfCardAnalyticsData,
            restaurantId,
        ],
    );

    const onSaveInstructions = useCallback(
        (note: string) => {
            if (cartResponse && !isRxInstructionsHalfCardXPEnabled) {
                clickSaveInstructions(restaurantId, note, clickEvent);
            }
        },
        [cartResponse, isRxInstructionsHalfCardXPEnabled, restaurantId, clickEvent],
    );

    const onImpressionInstructionHalfCard = useCallback(() => {
        if (cartResponse) {
            impressionHalfCardSpecialInstruction({
                isEdit: !!note,
                ...specialInstructionsHalfCardAnalyticsData,
                impressionEvent,
            });
        }
    }, [cartResponse, note, specialInstructionsHalfCardAnalyticsData, impressionEvent]);

    const onClickInstructionHalfCardAddRequest = useCallback(() => {
        if (cartResponse) {
            clickHalfCardSubmitRequest({
                ...specialInstructionsHalfCardAnalyticsData,
                clickEvent,
            });
        }
    }, [cartResponse, clickEvent, specialInstructionsHalfCardAnalyticsData]);

    const onClickInstructionHalfCardUpdateRequest = useCallback(() => {
        if (cartResponse) {
            clickHalfCardUpdateRequest({
                ...specialInstructionsHalfCardAnalyticsData,
                clickEvent,
            });
        }
    }, [cartResponse, clickEvent, specialInstructionsHalfCardAnalyticsData]);

    const onClickInstructionHalfCardRemoveRequest = useCallback(() => {
        if (cartResponse) {
            clickHalfCardRemoveRequest({
                ...specialInstructionsHalfCardAnalyticsData,
                clickEvent,
            });
        }
    }, [cartResponse, clickEvent, specialInstructionsHalfCardAnalyticsData]);

    const onClickInstructionHalfCardClose = useCallback(() => {
        if (cartResponse) {
            clickHalfCardClose({
                isEdit: !!note,
                ...specialInstructionsHalfCardAnalyticsData,
                clickEvent,
            });
        }
    }, [cartResponse, clickEvent, note, specialInstructionsHalfCardAnalyticsData]);

    return {
        onSpecialInstructionsImpression,
        onSpecialInstructionsClick,
        onSaveInstructions,
        onImpressionInstructionHalfCard,
        onClickInstructionHalfCardAddRequest,
        onClickInstructionHalfCardUpdateRequest,
        onClickInstructionHalfCardRemoveRequest,
        onClickInstructionHalfCardClose,
    };
}
